import { action, computed, extendObservable, observable, runInAction } from 'mobx';

import { SerializedData, Store } from 'bernie-core';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';

import { HotelViewModel } from 'models';
import { STORE_EVENTS } from './events/store-events';

import { ShortlistApiSource } from 'sources/shortlist-api-source';
import { ShortlistReplayApiSource } from 'sources/shortlist-replay-api-source';

export class ShortlistStore extends Store {
  constructor(
    private shortlistSource: ShortlistApiSource,
    private shortlistReplaySource: ShortlistReplayApiSource,
    state: SerializedData,
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    if (data.shortlistSource) {
      Object.assign(this.shortlistSource, {
        clientHeaders: data.shortlistSource.clientHeaders || {},
        clientQueryParams: data.shortlistSource.clientQueryParams || {}
      });
    }

    delete data.shortlistSource;
    delete data.shortlistReplaySource;
    Object.assign(this, data);
  }

  public static DEFAULTS = {
    isLoading: true,
    shouldFetchFreshData: false,
    hotel: {
      favoritedLocally: {},
      favorited: [],
      recentlyViewed: []
    },
    hotelIdFromMap: undefined
  };

  @observable
  public hotelIdFromMap: number;
  @observable
  public isLoading: boolean;
  @observable
  public shouldFetchFreshData: boolean;
  @observable
  public hotel: {
    favoritedLocally: any;
    favorited: HotelViewModel[];
    recentlyViewed: HotelViewModel[];
  };
  @observable
  public error: any;
  @observable
  public errors: any[];

  @computed
  get isError(): boolean {
    return Boolean(this.error || this.errors);
  }

  @computed
  get favoritedIds() {
    return this.hotel && this.hotel.favorited && this.hotel.favorited.map(hotel => hotel.id);
  }

  @computed
  get recentlyViewedIds() {
    return this.hotel && this.hotel.recentlyViewed && this.hotel.recentlyViewed.map(hotel => hotel.id);
  }

  @action
  public updateFavoritedLocally(id: any, status: boolean, category: string): void {
    const hotelId = Number(id);
    const updatedValue = {
      [hotelId]: status
    };
    extendObservable(this[category].favoritedLocally, updatedValue);
  }

  @action
  public updateFavorited(isFavorited, data, category) {
    if (isFavorited) {
      const favorited = this[category] && this[category].favorited;
      if (favorited && !favorited.some(e => e.id === data.id)) {
        this.shouldFetchFreshData = true;
        this[category].favorited.unshift(data);
      }
    } else {
      this[category].favorited = this[category].favorited.filter(item => item.id !== data.id);
    }

    this.hotelIdFromMap = data.id;
  }

  @action
  public resetHotelIdFromMap() {
    this.hotelIdFromMap = undefined;
  }

  @action
  public addToRecentlyViewed(data, category) {
    this.shouldFetchFreshData = true;
    const recentlyViewed = this[category] && this[category].recentlyViewed;
    if (recentlyViewed && !recentlyViewed.some(e => e.id === data.id)) {
      this[category].recentlyViewed.unshift(data);
    }
  }

  @action
  public resetReplayData(category) {
    this[category] = {
      ...this[category],
      favorited: [],
      recentlyViewed: []
    };
  }

  @action
  public removeReplayItem(context, id, category, removeUrl, meta) {
    const favorited = this[category] && this[category].favorited;
    const recentlyViewed = this[category] && this[category].recentlyViewed;

    if (favorited && favorited.some(e => e.id === id)) {
      this.fetch(removeUrl, context, meta, category);
      this[category].favorited = this[category].favorited.filter(item => item.id !== id);
    }

    if (recentlyViewed && recentlyViewed.some(e => e.id === id)) {
      this.fetchReplayRemoveHotels(context, id);
      this[category].recentlyViewed = this[category].recentlyViewed.filter(item => item.id !== id);
    }
  }

  @action
  public fetch(url, context, data, category): any {
    this.error = null;

    return this.shortlistSource
      .fetch(url, context, data, category)
      .then(resp => runInAction('successfully updated shortlist', () => resp))
      .catch(err =>
        runInAction('error during shortlist fetch', () => {
          if (this.logger) {
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
          }

          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  @action
  public fetchReplayHotels(context, category, searchCriteria, fallbackRegionId, inAppFetch = false): any {
    this.shouldFetchFreshData = false;
    this.error = null;
    this.isLoading = true;
    const searchParams = this.getSearchParams(searchCriteria, fallbackRegionId, inAppFetch);

    return this.shortlistReplaySource
      .fetch(context, searchParams)
      .then(data =>
        runInAction('successfully updated replay', () => {
          if (data && data.favorites) {
            if (inAppFetch) {
              this[category].favorited = this.mapHotelData(this[category].favorited, data.favorites);
            } else {
              this[category].favorited = data.favorites;
            }
          }

          if (data && data.recentlyViewed) {
            if (inAppFetch) {
              this[category].recentlyViewed = this.mapHotelData(this[category].recentlyViewed, data.recentlyViewed);
            } else {
              this[category].recentlyViewed = data.recentlyViewed;
            }
          }

          this.isLoading = false;

          return data;
        })
      )
      .catch(err =>
        runInAction('error during replay fetch', () => {
          if (this.logger) {
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
          }

          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  @action
  public fetchReplayRemoveHotels(context, hotelId): any {
    this.error = null;

    return this.shortlistReplaySource
      .fetchReplayRemoveHotels(context, hotelId)
      .then(data =>
        runInAction('successfully deleted replay hotel', () => {
          this.isLoading = false;

          return data;
        })
      )
      .catch(err =>
        runInAction('error during replay hotel deleted', () => {
          if (this.logger) {
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
          }

          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  public getSearchParams(searchCriteria, fallbackRegionId, inAppFetch) {
    const { startDate, endDate, adults, children } = searchCriteria;
    const regionId = searchCriteria.regionId || fallbackRegionId;
    const { favoritedIds, recentlyViewedIds } = this;

    const searchParams = {
      regionId,
      startDate,
      endDate,
      adults,
      children
    };

    if (inAppFetch) {
      return Object.assign({}, searchParams, { favoritedIds, recentlyViewedIds });
    }

    return searchParams;
  }

  public mapHotelData(currentHotelData, newHotelData) {
    return currentHotelData.map(currentHotel => {
      const matchingHotel = newHotelData.find(newHotel => newHotel.id === currentHotel.id);
      if (matchingHotel) {
        return matchingHotel;
      }

      return currentHotel;
    });
  }
}
